export interface BenefitSaveOutcome {
  saved: boolean
  /** The conflicting draft must close; never attach its old values to the reloaded token. */
  reloaded: boolean
  fieldErrors: Record<string, string[]>
  /** Observation ended with the old staff session; this is not proof of command failure. */
  observationEnded?: boolean
}

export interface BenefitSaveFailure {
  status?: number
  message: string
  fieldErrors: Record<string, string[]>
}

export async function runBenefitSave<T>(options: {
  write: () => Promise<T>
  reload: () => Promise<T>
  onUpdated: (value: T) => void
  onSaved: (value: T) => void
  onError: (message: string) => void
  classify: (error: unknown) => BenefitSaveFailure
  isCurrent?: () => boolean
  /** Workspace command ownership refreshes separately and retains the captured draft. */
  retainConflict?: boolean
}): Promise<BenefitSaveOutcome> {
  const ended = (): BenefitSaveOutcome => ({ saved: false, reloaded: false, fieldErrors: {}, observationEnded: true })
  if (options.isCurrent?.() === false) return ended()
  let updated: T
  try {
    updated = await options.write()
  } catch (error) {
    if (options.isCurrent?.() === false) return ended()
    const failure = options.classify(error)
    if (failure.status === 409 && !options.retainConflict) {
      try {
        const latest = await options.reload()
        if (options.isCurrent?.() === false) return ended()
        options.onUpdated(latest)
        options.onError(`${failure.message} The latest application is loaded; unsaved changes in this form were discarded. Review it before editing again.`)
        return { saved: false, reloaded: true, fieldErrors: {} }
      } catch {
        // A failed refresh must not replace the form's original token or hide the original error.
      }
    }
    if (options.isCurrent?.() === false) return ended()
    options.onError(failure.message)
    return { saved: false, reloaded: false, fieldErrors: failure.fieldErrors }
  }
  if (options.isCurrent?.() === false) return ended()
  options.onUpdated(updated)
  options.onSaved(updated)
  return { saved: true, reloaded: false, fieldErrors: {} }
}

export interface BenefitEditSession {
  editing: boolean
  token: string | null
}

/** Tokens are opaque and only echoed. An in-progress form never takes a new token from props. */
export function nextBenefitEditSession(current: BenefitEditSession, editing: boolean, latestToken: string | null): BenefitEditSession {
  return { editing, token: editing && !current.editing ? latestToken : current.token }
}
